import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { ErrorState } from '../../components/feedback/ErrorState';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton';
import { FeedbackDetailView } from '../../components/features/project-feedback/FeedbackDetailView.js';
import { useAuth } from '../../hooks/index.js';
import { useMemberList } from '../../hooks/useMember.js';
import {
  useProjectFeedbackDetail,
  useProjectFeedbackRevisions,
} from '../../hooks/useProjectFeedback.js';
import { ApiError } from '../../lib/api.js';
import { buildNicknameByEmail } from '../../lib/displayName.js';
import { createNameResolver, parseFeedbackDetailSearch } from '../../lib/project-feedback-view.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering, HR
 * @graph-business 案件 FB の詳細。good と more を並べ、根拠・評価者の判断・暫定スコア・判断できなかった観点・過去の版を見せる。本人は項目ごとに異議を出す・取り下げ、範囲内の評価者は項目ごとに判断を付ける（できることは API の permissions、判断の選択肢は verdictOptions に従う）
 * @graph-connects useProjectFeedbackDetail [calls] FB の詳細
 * @graph-connects useProjectFeedbackRevisions [calls] FB の過去の版
 * @graph-connects useMemberList [calls] 判断・異議を書いた人の表示名
 * @graph-connects FeedbackDetailView [calls] 詳細の表示
 */
export const Route = createFileRoute('/project-feedbacks/$feedbackId')({
  component: ProjectFeedbackDetailPage,
  validateSearch: parseFeedbackDetailSearch,
});

/**
 * 戻り先。本人はマイページの FB タブ、評価者は一覧。
 * @graph-connects none
 */
function BackLink({ isOwner }: { isOwner: boolean }) {
  return isOwner ? (
    <Link
      to="/my-page"
      search={{ tab: 'feedback' }}
      className="text-sm text-[var(--color-accent-primary)] hover:underline"
    >
      ← マイページ
    </Link>
  ) : (
    <Link
      to="/project-feedbacks"
      className="text-sm text-[var(--color-accent-primary)] hover:underline"
    >
      ← 一覧に戻る
    </Link>
  );
}

/**
 * 見えない FB と存在しない FB は API がどちらも 404 にするので、画面も同じ表示にする。
 * @graph-connects useAuth [calls] 本人判定・戻り先の決定
 * @graph-connects createNameResolver [calls] メールから表示名
 */
function ProjectFeedbackDetailPage() {
  const { feedbackId } = Route.useParams();
  const { version } = Route.useSearch();
  const navigate = useNavigate();
  const { user } = useAuth();
  const detail = useProjectFeedbackDetail(feedbackId);
  const revisions = useProjectFeedbackRevisions(feedbackId);
  const { data: members } = useMemberList();
  const nameOf = createNameResolver(
    buildNicknameByEmail([...(members?.engineers ?? []), ...(members?.qa ?? [])]),
  );
  const isOwner =
    detail.data !== undefined &&
    user?.email !== undefined &&
    user.email.toLowerCase() === detail.data.feedback.email.toLowerCase();

  if (detail.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <KpiTableSkeleton />
      </div>
    );
  }

  if (detail.error instanceof ApiError && detail.error.status === 404) {
    return (
      <div className="space-y-6 animate-fade-in">
        <BackLink isOwner />
        <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
          この案件 FB は見つかりません
        </Card>
      </div>
    );
  }

  if (detail.data === undefined) {
    return (
      <div className="space-y-6 animate-fade-in">
        <BackLink isOwner />
        <ErrorState
          onRetry={() => {
            void detail.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <BackLink isOwner={isOwner} />
      <FeedbackDetailView
        detail={detail.data}
        revisions={revisions.data?.revisions ?? []}
        version={version}
        isOwner={isOwner}
        nameOf={nameOf}
        onVersionChange={(next) => {
          void navigate({
            to: '/project-feedbacks/$feedbackId',
            params: { feedbackId },
            search: next === detail.data.feedback.version ? {} : { version: next },
            replace: true,
          });
        }}
      />
    </div>
  );
}
